// Vue
import { ref, watch, Ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Api
import {
  getInspectBuildingProjectTree,
  inspectBuildingProjectConstruction,
  inspectBuildingProjectEpd,
  inspectBuildingProjectMaterial,
  inspectBuildingProjectType,
} from "@/api/services";

import { getFile } from "@/api/services/File";

// Types
import { CarbonClientErrorType } from "@/api/types/ApiErrorTypes";
import {
  InspectNavigateType,
  InspectTreeNavigationType,
  InspectBuildingProjectConstructionRequestType,
  InspectBuildingProjectEpdRequestType,
  InspectBuildingProjectMaterialRequestType,
  InspectBuildingProjectTypeRequestType,
  InspectConfigurationType,
  InspectDataType,
  InspectIdentifierType,
  InspectToggleType,
  InspectTypeEnum,
} from "../types/InspectTypes";

interface InspectProvider {
  // Refs
  isInspecting: Ref<boolean>;
  inspectData: Ref<InspectDataType | undefined>;
  treeData: Ref<InspectTreeNavigationType | undefined>;
  treeNavigationIsActive: Ref<boolean>;
  currentConfiguration: Ref<InspectConfigurationType | undefined>;
  currentViewType: Ref<InspectIdentifierType | undefined>;
  currentlyInspecting: Ref<string | undefined>;

  // Methods
  toggleTreeNavigation: () => void;
  navigateInspect: (data: InspectNavigateType) => void;
  toggleInspect: (data: InspectToggleType) => void;
  closeInspect: () => void;
  openEpdDocumentationFile: (fileId: string) => void;

  // Utility
  getLoading: () => boolean;
  getUpdating: () => boolean;
  getDownloading: () => boolean;
  getError: () => boolean;
}

const useInspect = defineStore("InspectProvider", (): InspectProvider => {
  // ///////////////////
  // Data
  // ///////////////////

  const configurations: InspectConfigurationType[] = [
    {
      name: "projectBreakdown",
      showTree: true,
      views: {
        [InspectTypeEnum.TYPE]: {
          populate: async ({ buildingProjectId, indicatorTypeMappingId, inspectIdentifierId }) => {
            if (!buildingProjectId) return;

            // Get inspect data
            const inspect = await inspectBuildingProjectType({
              data: {
                indicatorTypeMappingId,
                typeIdentifier: inspectIdentifierId,
              } as InspectBuildingProjectTypeRequestType,
              buildingProjectId,
            });

            // Map into store
            // treeData.value = breakdown;
            inspectData.value = inspect;
          },
        },
        [InspectTypeEnum.CONSTRUCTION]: {
          populate: async ({ buildingProjectId, indicatorTypeMappingId, inspectIdentifierId }) => {
            if (!buildingProjectId) return;

            // Get inspect data
            const inspect = await inspectBuildingProjectConstruction({
              data: {
                indicatorTypeMappingId,
                constructionId: inspectIdentifierId,
              } as InspectBuildingProjectConstructionRequestType,
              buildingProjectId,
            });

            // Map into store
            // treeData.value = breakdown;
            inspectData.value = inspect;
          },
        },
        [InspectTypeEnum.MATERIAL]: {
          populate: async ({ buildingProjectId, indicatorTypeMappingId, inspectIdentifierId }) => {
            if (!buildingProjectId) return;

            // Get inspect data
            const inspect = await inspectBuildingProjectMaterial({
              data: {
                indicatorTypeMappingId,
                materialId: inspectIdentifierId,
              } as InspectBuildingProjectMaterialRequestType,
              buildingProjectId,
            });

            // Map into store
            // treeData.value = breakdown;
            inspectData.value = inspect;
          },
        },
        [InspectTypeEnum.EPD]: {
          populate: async ({ buildingProjectId, indicatorTypeMappingId, inspectIdentifierId }) => {
            if (!buildingProjectId) return;

            // Get inspect data
            const inspect = await inspectBuildingProjectEpd({
              data: {
                indicatorTypeMappingId,
                epdId: inspectIdentifierId,
              } as InspectBuildingProjectEpdRequestType,
              buildingProjectId,
            });

            // Map into store
            // treeData.value = breakdown;
            inspectData.value = inspect;
          },
        },
      },
    },
    {
      name: "epd",
      showTree: false,
      views: {
        [InspectTypeEnum.EPD]: {
          populate: async () => {},
        },
      },
    },
  ];

  // ///////////////////
  // Refs
  // ///////////////////

  const inspectData = ref<InspectDataType>();
  const treeData = ref<InspectTreeNavigationType>();
  const currentlyInspecting = ref<string | undefined>();
  const isInspecting = ref<boolean>(false);
  const treeNavigationIsActive = ref<boolean>(false);
  const currentConfiguration = ref<InspectConfigurationType>();
  const currentViewType = ref<InspectIdentifierType>();
  const currentBuildingProjectId = ref<string | undefined>();
  const currentIndicatorTypeMappingId = ref<string | undefined>();

  // ///////////////////
  // Getters
  // ///////////////////

  // ///////////////////
  // Setters
  // ///////////////////

  // ///////////////////
  // Methods
  // ///////////////////

  async function navigateInspect({ inspectIdentifier, inspectIdentifierId }: InspectNavigateType) {
    // Clear error
    fetchError.value = false;

    // Start loading
    fetchUpdating.value = true;

    // Set current view
    currentViewType.value = inspectIdentifier;

    // Set currently inspecting
    currentlyInspecting.value = inspectIdentifierId;

    // Populate data based on configuration
    if (currentViewType.value) {
      await currentConfiguration.value?.views[currentViewType.value]?.populate({
        buildingProjectId: currentBuildingProjectId.value,
        indicatorTypeMappingId: currentIndicatorTypeMappingId.value,
        inspectIdentifierId,
      });
    }

    // Toggle tree navigation
    if (currentConfiguration.value?.showTree) {
      toggleTreeNavigation();
    }

    // Stop loading
    fetchUpdating.value = false;
  }

  function toggleTreeNavigation() {
    treeNavigationIsActive.value = !treeNavigationIsActive.value;
  }

  async function toggleInspect({
    buildingProjectId,
    typeIdentifier,
    configuration,
    inspectIdentifier,
    inspectIdentifierId,
    indicatorTypeMappingId,
  }: InspectToggleType) {
    // Clear error
    fetchError.value = false;

    // Start loading
    fetchLoading.value = true;

    // Toggle is inspecting (loading wrapper will appear)
    isInspecting.value = !isInspecting.value;

    // Set current building project id
    currentBuildingProjectId.value = buildingProjectId;

    // Set current indicator type mapping id
    currentIndicatorTypeMappingId.value = indicatorTypeMappingId;

    // Get current configuration based on configuration
    const currentConfig = configurations.find((config) => config.name === configuration);

    // Set currentConfiguration
    currentConfiguration.value = currentConfig;

    // If showTree is true, get breakdown data
    if (currentConfig?.showTree && buildingProjectId) {
      // Get breakdown data
      const tree = await getInspectBuildingProjectTree({
        data: {
          typeIdentifier,
        },
        buildingProjectId,
      });

      // Map into store
      treeData.value = tree;
    }

    // Set current view
    currentViewType.value = inspectIdentifier;

    // Set currently inspecting
    currentlyInspecting.value = inspectIdentifierId;

    // Populate data based on configuration
    if (currentViewType.value) {
      await currentConfig?.views[currentViewType.value]?.populate({
        buildingProjectId,
        indicatorTypeMappingId,
        inspectIdentifierId,
      });
    }

    // Stop loading
    fetchLoading.value = false;
  }

  async function openEpdDocumentationFile(fileId: string) {
    // Start updating
    fetchDownloading.value = true;

    // Get file
    const file = await getFile(fileId);

    /// Create a Blob object from the PDF data
    const blob = new Blob([file], { type: "application/pdf" });

    // Create a URL for the Blob object
    const url = window.URL.createObjectURL(blob);

    // Open the PDF in a new tab
    window.open(url, "_blank");

    // Optionally, revoke the URL to avoid memory leaks
    window.URL.revokeObjectURL(url);

    // Stop updating
    fetchDownloading.value = false;
  }

  function closeInspect() {
    // Close
    isInspecting.value = false;

    // Reset
    reset();
  }

  function reset() {
    inspectData.value = undefined;
    treeData.value = undefined;
    currentConfiguration.value = undefined;
    currentViewType.value = undefined;
    currentlyInspecting.value = undefined;
    currentBuildingProjectId.value = undefined;
    currentIndicatorTypeMappingId.value = undefined;
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const fetchLoading = ref<boolean>(false);
  const fetchUpdating = ref<boolean>(false);
  const fetchDownloading = ref<boolean>(false);
  const fetchError = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const updating = ref<boolean>(false);
  const error = ref<boolean>(false);

  function getError() {
    return error.value;
  }

  function getLoading() {
    return loading.value;
  }

  function getUpdating() {
    return updating.value;
  }

  function getDownloading() {
    return fetchDownloading.value;
  }

  // Check inspectData for Error
  watch(inspectData, (newInspectData) => {
    if (!newInspectData) return;

    const typedInspectData = newInspectData as unknown as CarbonClientErrorType;

    if (typedInspectData?.error) {
      error.value = true;
    } else {
      error.value = false;
    }
  });

  watch([fetchLoading], ([newLoading1]) => {
    loading.value = newLoading1;
  });

  watch([fetchUpdating], ([newUpdating1]) => {
    updating.value = newUpdating1;
  });

  watch([fetchDownloading], ([newDownloading1]) => {
    updating.value = newDownloading1;
  });

  watch([fetchError], ([newFetchError]) => {
    console.error({ newFetchError });
    error.value = newFetchError;
  });

  // ///////////////////
  // Return
  // ///////////////////

  return {
    // Refs
    isInspecting,
    inspectData,
    treeData,
    treeNavigationIsActive,
    currentConfiguration,
    currentViewType,
    currentlyInspecting,

    // Methods
    toggleTreeNavigation,
    navigateInspect,
    toggleInspect,
    closeInspect,
    openEpdDocumentationFile,

    // Utility
    getLoading,
    getUpdating,
    getDownloading,
    getError,
  };
});

export default useInspect;
